import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Bot, Plus, Trash2, ArrowLeft, Loader2, Upload, ImageIcon, FileText,
  Code, MessageSquare, Save, ShoppingBag,
} from 'lucide-react';
import chatbotService, { type ChatbotLicense, type ChatbotConversation, type ChatbotMessage } from '@/services/chatbot.service';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// Gömülü widget'ın servis edildiği taban URL (widget-api).
const WIDGET_SRC = 'https://svc-widget-api-e1as56.dockup.tech/api/widget.js';

const DEFAULT_METADATA = {
  plan: 'premium',
  widget_theme: 'light',
  widget_title: 'KI-Assistent',
  widget_primary_color: '#0F99FD',
  widget_welcome_message: 'Hallo! 👋\n\nWie können wir Ihnen helfen?',
  widget_training_data: '',
  widget_send_button_text: 'Senden',
  widget_thinking_text: 'Denkt nach...',
  widget_input_placeholder: 'Nachricht schreiben...',
};

type Tab = 'config' | 'knowledge' | 'avatar' | 'catalog' | 'shop' | 'install' | 'conversations';

export default function ChatbotSection() {
  const { t } = useTranslation();
  const [licenses, setLicenses] = useState<ChatbotLicense[]>([]);
  const [quota, setQuota] = useState({ maxLicenses: 0, currentCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newDomain, setNewDomain] = useState('');

  const loadLicenses = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await chatbotService.getLicenses();
      setLicenses(res.data?.licenses || []);
      setQuota(res.data?.user || { maxLicenses: 0, currentCount: 0 });
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Chatbot-Dienst nicht erreichbar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLicenses(); }, []);

  const handleCreate = async () => {
    if (!newDomain.trim()) return;
    setCreating(true);
    try {
      await chatbotService.createLicense(newDomain.trim(), { ...DEFAULT_METADATA });
      setNewDomain('');
      await loadLicenses();
      toast.success(t('common:x1.widgetCreated', 'Widget erstellt'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erstellen fehlgeschlagen');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('common:x1.confirmDeleteWidget', 'Dieses Widget wirklich löschen?'))) return;
    try {
      await chatbotService.deleteLicense(id);
      await loadLicenses();
      toast.success(t('common:x1.widgetDeleted', 'Widget gelöscht'));
    } catch (e: any) {
      toast.error(t('common:x1.deleteFailed', 'Löschen fehlgeschlagen'));
    }
  };

  if (selectedId) {
    return <LicenseEditor licenseId={selectedId} onBack={() => { setSelectedId(null); loadLicenses(); }} />;
  }

  const atLimit = quota.maxLicenses > 0 && quota.currentCount >= quota.maxLicenses;

  return (
    <div className="space-y-6">
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardHeader className="border-b border-slate-100 bg-white">
          <CardTitle className="flex items-center text-lg">
            <div className="w-10 h-10 bg-[#0f172a] rounded-lg flex items-center justify-center mr-3">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-gray-900">{t('common:x1.chatbotWidgets', 'Chatbot Widgets')}</div>
              <CardDescription className="text-sm mt-1">
                {t('common:x2.chatbotSectionDesc', 'Verwalten Sie Ihre Website-Chatbots — Aussehen, Wissen, Integrationen')}
              </CardDescription>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          {/* Quota + create */}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="text-sm text-slate-500">
              {quota.currentCount} / {quota.maxLicenses || '∞'} Widgets genutzt
            </div>
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <Label htmlFor="new-domain" className="text-xs">{t('common:x1.newDomain', 'Neue Domain')}</Label>
                <Input
                  id="new-domain"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="z.B. shop.kunde.de"
                  className="w-56"
                  disabled={atLimit}
                />
              </div>
              <Button onClick={handleCreate} disabled={creating || atLimit || !newDomain.trim()}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                <span className="ml-1">{atLimit ? 'Limit erreicht' : 'Hinzufügen'}</span>
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
          ) : licenses.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 py-12 text-center text-sm text-slate-400">
              {t('common:x1.noChatbotWidget', 'Noch kein Chatbot-Widget. Füge oben eine Domain hinzu.')}
            </div>
          ) : (
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-100">
              {licenses.map((lic) => (
                <div key={lic.id} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-800">{lic.domain || '—'}</div>
                    <div className="truncate text-xs text-slate-400">{lic.metadata?.widget_title || 'KI-Assistent'}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setSelectedId(lic.id)}>{t('common:x1.manage', 'Verwalten')}</Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(lic.id)}>
                      <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function LicenseEditor({ licenseId, onBack }: { licenseId: string; onBack: () => void }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('config');
  const [lic, setLic] = useState<ChatbotLicense | null>(null);
  const [meta, setMeta] = useState<Record<string, any>>({});
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [productCount, setProductCount] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await chatbotService.getLicense(licenseId);
      const l: ChatbotLicense = res.data;
      let m = l.metadata;
      if (typeof m === 'string') { try { m = JSON.parse(m); } catch { m = {}; } }
      setLic(l);
      setMeta({ ...DEFAULT_METADATA, ...(m || {}) });
      setDomain(l.domain || '');
      const p = await chatbotService.getProducts(licenseId).catch(() => null);
      setProductCount(p?.data?.productCount ?? null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [licenseId]);

  const save = async () => {
    setSaving(true);
    try {
      await chatbotService.updateLicense(licenseId, { domain, metadata: meta });
      toast.success(t('common:x1.saved', 'Gespeichert'));
    } catch {
      toast.error(t('common:state.saveFailed', 'Speichern fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  const set = (k: string, v: any) => setMeta((prev) => ({ ...prev, [k]: v }));

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: 'config', label: 'Erscheinung', icon: Bot },
    { id: 'knowledge', label: 'Wissen', icon: FileText },
    { id: 'avatar', label: 'Avatar', icon: ImageIcon },
    { id: 'catalog', label: 'Produkte', icon: Upload },
    { id: 'shop', label: 'Shop', icon: ShoppingBag },
    { id: 'install', label: 'Installation', icon: Code },
    { id: 'conversations', label: 'Gespräche', icon: MessageSquare },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> {t('common:action.back', 'Zurück')}
        </button>
        <div className="text-sm font-semibold text-slate-700">{domain || lic?.domain}</div>
      </div>

      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <div className="flex flex-wrap gap-1 border-b border-slate-100 px-3 pt-3">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition ${
                tab === t.id ? 'border-b-2 border-[#0F99FD] text-[#0F99FD]' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <t.icon className="h-4 w-4" />{t.label}
            </button>
          ))}
        </div>
        <CardContent className="pt-6">
          {tab === 'config' && (
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-3">
                <Field label="Domain"><Input value={domain} onChange={(e) => setDomain(e.target.value)} /></Field>
                <Field label="Theme">
                  <select value={meta.widget_theme} onChange={(e) => set('widget_theme', e.target.value)}
                    className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
                    <option value="light">{t('common:x1.light', 'Hell')}</option>
                    <option value="dark">{t('common:x1.dark', 'Dunkel')}</option>
                  </select>
                </Field>
                <Field label="Titel"><Input value={meta.widget_title} onChange={(e) => set('widget_title', e.target.value)} /></Field>
                <Field label="Primärfarbe">
                  <div className="flex items-center gap-2">
                    <input type="color" value={meta.widget_primary_color} onChange={(e) => set('widget_primary_color', e.target.value)}
                      className="h-10 w-12 rounded border border-slate-200" />
                    <Input value={meta.widget_primary_color} onChange={(e) => set('widget_primary_color', e.target.value)} />
                  </div>
                </Field>
                <Field label="Willkommensnachricht">
                  <Textarea rows={3} value={meta.widget_welcome_message} onChange={(e) => set('widget_welcome_message', e.target.value)} />
                </Field>
                <Field label="Senden-Button"><Input value={meta.widget_send_button_text} onChange={(e) => set('widget_send_button_text', e.target.value)} /></Field>
                <Field label="„Denkt nach“-Text"><Input value={meta.widget_thinking_text} onChange={(e) => set('widget_thinking_text', e.target.value)} /></Field>
                <Field label="Eingabe-Platzhalter"><Input value={meta.widget_input_placeholder} onChange={(e) => set('widget_input_placeholder', e.target.value)} /></Field>
              </div>
              {/* Live preview */}
              <div>
                <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                  <div className="px-4 py-3 text-white" style={{ background: meta.widget_primary_color }}>
                    <div className="text-sm font-semibold">{meta.widget_title}</div>
                  </div>
                  <div className="space-y-2 bg-slate-50 p-4" style={{ minHeight: 220 }}>
                    <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-white px-3 py-2 text-sm shadow-sm whitespace-pre-line">
                      {meta.widget_welcome_message}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 border-t border-slate-100 bg-white p-3">
                    <div className="flex-1 rounded-full bg-slate-100 px-3 py-2 text-xs text-slate-400">{meta.widget_input_placeholder}</div>
                    <span className="rounded-full px-3 py-1.5 text-xs font-semibold text-white" style={{ background: meta.widget_primary_color }}>
                      {meta.widget_send_button_text}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'knowledge' && (
            <Field label="Wissensdatenbank / Trainingsdaten">
              <Textarea rows={14} className="font-mono text-xs" value={meta.widget_training_data}
                onChange={(e) => set('widget_training_data', e.target.value)}
                placeholder="Produktinfos, FAQ, Richtlinien … Der Chatbot nutzt diesen Text als Wissen." />
            </Field>
          )}

          {tab === 'avatar' && <AvatarTab licenseId={licenseId} meta={meta} onChange={load} />}
          {tab === 'catalog' && <CatalogTab licenseId={licenseId} productCount={productCount} onChange={load} />}
          {tab === 'shop' && <ShopTab licenseId={licenseId} />}
          {tab === 'install' && <InstallTab licenseKey={lic?.license_key} />}
          {tab === 'conversations' && <ConversationsTab licenseId={licenseId} />}

          {(tab === 'config' || tab === 'knowledge') && (
            <div className="mt-6 flex justify-end">
              <Button onClick={save} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span className="ml-1">{t('common:action.save', 'Speichern')}</span>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function AvatarTab({ licenseId, meta, onChange }: { licenseId: string; meta: Record<string, any>; onChange: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  // Cache-bust: aynı URL'i tarayıcı cache'lemesin (re-upload sonrası eski resim sorunu)
  const url = meta.widget_avatar_url ? `${meta.widget_avatar_url}?t=${Date.now()}` : null;
  const upload = async (f?: File | null) => {
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error(t('common:x1.max2mb', 'Max. 2MB')); return; }
    setBusy(true);
    try { await chatbotService.uploadAvatar(licenseId, f); toast.success(t('common:x1.avatarUploaded', 'Avatar hochgeladen')); onChange(); }
    catch { toast.error(t('common:x1.uploadFailed', 'Upload fehlgeschlagen')); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      {url ? (
        <div className="flex items-center gap-4">
          <img src={url} alt="" className="h-16 w-16 rounded-full object-cover ring-1 ring-slate-200" />
          <Button variant="outline" size="sm" onClick={async () => { await chatbotService.deleteAvatar(licenseId); onChange(); }}>
            <Trash2 className="h-4 w-4" /> {t('ticket:action.remove', 'Entfernen')}
          </Button>
        </div>
      ) : (
        <div className="text-sm text-slate-400">{t('common:x1.noAvatar', 'Kein Avatar gesetzt.')}</div>
      )}
      <input ref={ref} type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} />
      <Button variant="outline" onClick={() => ref.current?.click()} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        <span className="ml-1">{t('common:x1.uploadImageHint', 'Bild hochladen (≤2MB, quadratisch)')}</span>
      </Button>
    </div>
  );
}

function CatalogTab({ licenseId, productCount, onChange }: { licenseId: string; productCount: number | null; onChange: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const upload = async (f?: File | null) => {
    if (!f) return;
    setBusy(true);
    try { await chatbotService.uploadProducts(licenseId, f); toast.success(t('common:x1.catalogUploaded', 'Katalog hochgeladen')); onChange(); }
    catch { toast.error(t('common:x1.uploadFailed', 'Upload fehlgeschlagen')); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      {productCount && productCount > 0 ? (
        <div className="flex items-center gap-3">
          <Badge>{productCount} Produkte</Badge>
          <Button variant="outline" size="sm" onClick={async () => { await chatbotService.deleteProducts(licenseId); onChange(); }}>
            <Trash2 className="h-4 w-4" /> {t('common:x1.deleteCatalog', 'Katalog löschen')}
          </Button>
        </div>
      ) : (
        <div className="text-sm text-slate-400">Kein Produktkatalog. Lade eine CSV hoch (Spalten: id, name, description, price, category).</div>
      )}
      <input ref={ref} type="file" accept=".csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      <Button variant="outline" onClick={() => ref.current?.click()} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        <span className="ml-1">{t('common:x1.uploadCsv', 'CSV hochladen')}</span>
      </Button>
    </div>
  );
}

function InstallTab({ licenseKey }: { licenseKey?: string }) {
  const { t } = useTranslation();
  const snippet = `<script src="${WIDGET_SRC}" data-license="${licenseKey || 'IHR-LIZENZSCHLÜSSEL'}" async></script>`;
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">{t('common:x1.embedCodeBefore', 'Fügen Sie diesen Code vor')} <code>&lt;/body&gt;</code> {t('common:x1.embedCodeAfter', 'Ihrer Website ein:')}</p>
      <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs text-slate-100"><code>{snippet}</code></pre>
      <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(snippet); toast.success(t('common:x1.copied', 'Kopiert')); }}>
        <Code className="h-4 w-4" /> {t('common:x1.copyCode', 'Code kopieren')}
      </Button>
    </div>
  );
}

function ShopTab({ licenseId }: { licenseId: string }) {
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [type, setType] = useState<'shopify' | 'jtl'>('shopify');
  const [form, setForm] = useState<Record<string, string>>({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await chatbotService.getShopIntegrations(licenseId);
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [licenseId]);

  const setF = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const create = async () => {
    setBusy(true);
    try {
      const payload: Record<string, any> =
        type === 'shopify'
          ? { integration_type: 'shopify', shop_url: form.shop_url, access_token: form.access_token, api_version: form.api_version || '2023-10', webhook_secret: form.webhook_secret || null }
          : { integration_type: 'jtl', api_url: form.api_url, api_key: form.api_key };
      await chatbotService.createShopIntegration(licenseId, payload);
      toast.success(t('common:x1.shopConnSaved', 'Shop-Verbindung gespeichert'));
      setForm({});
      setAdding(false);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm(t('common:x1.deleteConnQ', 'Verbindung löschen?'))) return;
    await chatbotService.deleteShopIntegration(licenseId, id);
    load();
  };

  if (loading) return <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Shopify oder JTL verbinden — der Chatbot beantwortet damit Bestell- & Lieferstatus-Fragen.</p>

      {items.length > 0 && (
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-100">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge>{it.integration_type?.toUpperCase()}</Badge>
                  <span className={`h-2 w-2 rounded-full ${it.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                </div>
                <div className="mt-1 truncate text-xs text-slate-500">{it.shop_url || it.api_url || '—'}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(it.id)}>
                <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="space-y-3 rounded-lg border border-slate-200 p-4">
          <Field label="Typ">
            <select value={type} onChange={(e) => setType(e.target.value as any)}
              className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
              <option value="shopify">Shopify</option>
              <option value="jtl">JTL</option>
            </select>
          </Field>
          {type === 'shopify' ? (
            <>
              <Field label="Shop URL"><Input placeholder="ihr-shop.myshopify.com" value={form.shop_url || ''} onChange={(e) => setF('shop_url', e.target.value)} /></Field>
              <Field label="Access Token"><Input placeholder="shpat_..." value={form.access_token || ''} onChange={(e) => setF('access_token', e.target.value)} /></Field>
              <Field label="API Version"><Input placeholder="2023-10" value={form.api_version || ''} onChange={(e) => setF('api_version', e.target.value)} /></Field>
              <Field label="Webhook Secret (optional)"><Input value={form.webhook_secret || ''} onChange={(e) => setF('webhook_secret', e.target.value)} /></Field>
            </>
          ) : (
            <>
              <Field label="API URL"><Input placeholder="https://..." value={form.api_url || ''} onChange={(e) => setF('api_url', e.target.value)} /></Field>
              <Field label="API Key"><Input value={form.api_key || ''} onChange={(e) => setF('api_key', e.target.value)} /></Field>
            </>
          )}
          <div className="flex gap-2">
            <Button onClick={create} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}<span className="ml-1">{t('common:action.save', 'Speichern')}</span></Button>
            <Button variant="outline" onClick={() => { setAdding(false); setForm({}); }}>{t('common:action.cancel', 'Abbrechen')}</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> {t('common:x1.connectShop', 'Shop verbinden')}</Button>
      )}
    </div>
  );
}

function ConversationsTab({ licenseId }: { licenseId: string }) {
  const { t } = useTranslation();
  const [convs, setConvs] = useState<ChatbotConversation[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<ChatbotMessage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const r = await chatbotService.getConversations(licenseId); setConvs(r.data || []); }
      finally { setLoading(false); }
    })();
  }, [licenseId]);

  const open = async (id: string) => {
    setActive(id);
    const r = await chatbotService.getMessages(id);
    setMsgs(r.data || []);
  };

  if (loading) return <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (convs.length === 0) return <div className="py-10 text-center text-sm text-slate-400">{t('common:x1.noConversations', 'Noch keine Gespräche.')}</div>;

  return (
    <div className="grid gap-4 md:grid-cols-[260px_1fr]">
      <div className="max-h-[420px] overflow-y-auto rounded-lg border border-slate-100 divide-y divide-slate-100">
        {convs.map((c) => (
          <button key={c.id} onClick={() => open(c.id)}
            className={`block w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50 ${active === c.id ? 'bg-slate-50' : ''}`}>
            <div className="truncate font-medium text-slate-700">{c.title || c.domain || 'Gespräch'}</div>
            <div className="text-xs text-slate-400">{c.message_count} Nachrichten</div>
          </button>
        ))}
      </div>
      <div className="max-h-[420px] overflow-y-auto rounded-lg border border-slate-100 p-3">
        {!active ? (
          <div className="py-10 text-center text-sm text-slate-400">{t('common:x1.selectConversation', 'Gespräch auswählen.')}</div>
        ) : (
          <div className="space-y-2">
            {msgs.map((m) => (
              <div key={m.id} className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                m.role === 'user' ? 'ml-auto bg-[#0F99FD] text-white' : 'bg-slate-100 text-slate-800'
              }`}>{m.content}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
